import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Avatar,
  useTheme,
  alpha,
  Fade,
  IconButton,
  Tooltip,
  Button,
  Stack,
  Divider,
  LinearProgress,
  Chip,
  Container,
  Tabs,
  Tab,
  Skeleton,
  useMediaQuery,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Badge,
  Alert,
  AlertTitle,
  Select,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  Autocomplete,
  TextField
} from '@mui/material';
import {
  Hotel as HotelIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  BusinessCenter as BusinessCenterIcon,
  MonetizationOn as MonetizationOnIcon,
  StarOutline as StarOutlineIcon,
  Refresh as RefreshIcon,
  Info as InfoIcon,
  Assessment as AssessmentIcon,
  LocationOn as LocationOnIcon,
  CalendarToday as CalendarTodayIcon,
  Build as BuildIcon,
  Analytics as AnalyticsIcon,
  ViewModule as ViewModuleIcon,
  ViewList as ViewListIcon,
  Add as AddIcon,
  FilterList as FilterListIcon,
  Search as SearchIcon,
  ArrowDropDown as ArrowDropDownIcon,
  Upload as UploadIcon,
  AutoAwesome as AutoAwesomeIcon,
  GetApp as GetAppIcon,
  PushPin as PushPinIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  PlayArrow as PlayArrowIcon,
  Notifications as NotificationsIcon,
  CurrencyExchange as CurrencyExchangeIcon,
  Sort as SortIcon,
  LightbulbOutlined as LightbulbOutlinedIcon
} from '@mui/icons-material';

// Import hooks and services
import { useHotelList } from '../hooks/useHotelList';
import hotelService from '../services/hotelService';
import { formatCurrency } from '@utils/financialUtils';
import { Hotel } from '../../../types/types';

// Import sub-components
import HotelGrid from '@features/hotels/components/HotelGrid';
import HotelTable from '@features/hotels/components/HotelTable';
import PortfolioInsights from '@features/hotels/components/PortfolioInsights';

interface PortfolioStats {
  totalProperties: number;
  totalRooms: number;
  totalValue: number;
  averageStarRating: number;
  totalRenovationCost: number;
  averageOccupancy: number;
  revenueGrowth: number;
  portfolioAge: number;
}

interface TrendData {
  value: number;
  isPositive: boolean;
  previousPeriod?: string;
  sparklineData?: number[];
}

const PortfolioDashboard: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid');
  const [activeTab, setActiveTab] = useState(0);
  const [addMenuAnchor, setAddMenuAnchor] = useState<null | HTMLElement>(null);
  const [sortBy, setSortBy] = useState<string>('lastUpdated');
  const [currency, setCurrency] = useState<'USD' | 'EUR'>('USD');
  const [showAlerts, setShowAlerts] = useState(true);
  const [pinnedProperties, setPinnedProperties] = useState<Set<string>>(new Set());

  // Use hotel list hook
  const {
    hotels,
    loading: hotelsLoading
  } = useHotelList();

  // Mock trend data for demonstration
  const generateSparklineData = () => [65, 72, 68, 75, 78, 82, 85];

  // Calculate portfolio statistics with enhanced trends
  const portfolioStats: PortfolioStats = useMemo(() => {
    if (!hotels.length) {
      return {
        totalProperties: 0,
        totalRooms: 0,
        totalValue: 0,
        averageStarRating: 0,
        totalRenovationCost: 0,
        averageOccupancy: 0,
        revenueGrowth: 0,
        portfolioAge: 0
      };
    }

    const totalRooms = hotels.reduce((sum, hotel) => sum + (hotel.num_rooms || 0), 0);
    const totalValue = hotels.reduce((sum, hotel) => {
      const value = parseFloat(hotel.estimated_value?.toString() || '0');
      console.log(`🔢 Processing ${hotel.name}: estimated_value="${hotel.estimated_value}" (${typeof hotel.estimated_value}) -> parsed=${value}`);
      return sum + value;
    }, 0);
    const averageStarRating = hotels.reduce((sum, hotel) => sum + (hotel.star_rating || 0), 0) / hotels.length;

    const totalRenovationCost = hotels.reduce((sum, hotel) => {
      return sum + (hotel.renovation_history?.reduce((renovSum, renovation) =>
        renovSum + (renovation.cost || 0), 0) || 0);
    }, 0);

    // Calculate portfolio age (average years since built)
    const currentYear = new Date().getFullYear();
    const averageAge = hotels.reduce((sum, hotel) => {
      const yearBuilt = hotel.year_built || currentYear;
      return sum + (currentYear - yearBuilt);
    }, 0) / hotels.length;

    return {
      totalProperties: hotels.length,
      totalRooms,
      totalValue,
      averageStarRating,
      totalRenovationCost,
      averageOccupancy: 75.0, // Mock data - would come from actual occupancy data
      revenueGrowth: 8.5, // Mock data - would come from financial analysis
      portfolioAge: Math.round(averageAge)
    };
  }, [hotels]);

  // Enhanced metrics with trend data
  const enhancedMetrics = useMemo(() => [
    {
      title: 'Portfolio Value',
      value: formatCurrency(portfolioStats.totalValue, currency),
      subtitle: 'Total market value',
      icon: MonetizationOnIcon,
      trend: {
        value: portfolioStats.revenueGrowth,
        isPositive: portfolioStats.revenueGrowth > 0,
        previousPeriod: 'vs. previous quarter',
        sparklineData: generateSparklineData()
      }
    },
    {
      title: 'Total Properties',
      value: portfolioStats.totalProperties,
      subtitle: `${portfolioStats.totalRooms.toLocaleString()} rooms`,
      icon: HotelIcon,
      trend: {
        value: 12.5,
        isPositive: true,
        previousPeriod: 'vs. last month',
        sparklineData: [8, 9, 10, 11, 12, 13, portfolioStats.totalProperties]
      }
    },
    {
      title: 'Avg. Occupancy',
      value: `${portfolioStats.averageOccupancy}%`,
      subtitle: 'Performance metric',
      icon: AssessmentIcon,
      trend: {
        value: 5.2,
        isPositive: true,
        previousPeriod: 'vs. industry avg',
        sparklineData: [70, 72, 74, 73, 75, 76, 75]
      }
    },
    {
      title: 'Avg. Property Age',
      value: `${portfolioStats.portfolioAge} years`,
      subtitle: 'Average across portfolio',
      icon: CalendarTodayIcon,
      trend: {
        value: -2.1,
        isPositive: false,
        previousPeriod: 'change this year',
        sparklineData: [22, 21.5, 21, 20.8, 20.5, 20.2, portfolioStats.portfolioAge]
      }
    }
  ], [portfolioStats, currency]);

  // Dynamic alerts based on actual hotel data
  const alerts = useMemo(() => {
    const dynamicAlerts = [];

    // Debug: Log hotel data to diagnose N/A values
    console.log('🔍 PortfolioDashboard hotels data:', {
      count: hotels.length,
      hotels: hotels.map(h => ({
        id: h.id,
        name: h.name,
        estimated_value: h.estimated_value,
        estimated_value_type: typeof h.estimated_value,
        last_valuation_date: h.last_valuation_date,
        raw_hotel_object: h
      }))
    });

    // Debug: Log portfolio stats calculation
    console.log('💰 Portfolio stats calculation:', {
      totalValue: portfolioStats.totalValue,
      calculation: hotels.map(h => `${h.name}: ${h.estimated_value} (${typeof h.estimated_value})`)
    });

    if (hotels.length > 0) {
      // Check for hotels without P&L data (no estimated_value)
      const hotelsWithoutValues = hotels.filter(h => !h.estimated_value);
      if (hotelsWithoutValues.length > 0) {
        dynamicAlerts.push({
          id: 1,
          type: 'warning',
          message: `${hotelsWithoutValues.length} ${hotelsWithoutValues.length === 1 ? 'property lacks' : 'properties lack'} valuation data`,
          action: 'Update Financial Data'
        });
      }

      // Check for hotels without recent valuation updates
      const oldValuationThreshold = new Date(Date.now() - 90 * 24 * 60 * 60 * 1000); // 90 days ago
      const hotelsNeedingUpdate = hotels.filter(h => {
        if (!h.last_valuation_date) return true;
        return new Date(h.last_valuation_date) < oldValuationThreshold;
      });

      if (hotelsNeedingUpdate.length > 0) {
        dynamicAlerts.push({
          id: 2,
          type: 'info',
          message: `${hotelsNeedingUpdate.length} ${hotelsNeedingUpdate.length === 1 ? 'hotel needs' : 'hotels need'} updated analysis`,
          action: 'Run Analysis'
        });
      }

      // Success message for recent valuations
      const recentValuations = hotels.filter(h => {
        if (!h.last_valuation_date) return false;
        const recentThreshold = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000); // 7 days ago
        return new Date(h.last_valuation_date) > recentThreshold;
      });

      if (recentValuations.length > 0) {
        dynamicAlerts.push({
          id: 3,
          type: 'success',
          message: `${recentValuations.length} ${recentValuations.length === 1 ? 'property has' : 'properties have'} recent valuations`,
          action: 'View Details'
        });
      }
    }

    return dynamicAlerts;
  }, [hotels]);

  // Handle refresh with actual data refresh
  const handleRefresh = async () => {
    setLoading(true);
    try {
      console.log('🔄 Refreshing portfolio data...');

      // Force refresh hotel data with cache busting
      const refreshedData = await hotelService.getHotels();
      console.log('✅ Refreshed hotel data:', {
        count: refreshedData.length,
        totalValue: refreshedData.reduce((sum: number, hotel: Hotel) => sum + (hotel.estimated_value || 0), 0)
      });

      // Trigger a re-fetch in the useHotelList hook by adding a refresh method
      window.location.reload(); // Temporary solution to force refresh

    } catch (error) {
      console.error('❌ Error refreshing data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handle add hotel menu
  const handleAddMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAddMenuAnchor(event.currentTarget);
  };

  const handleAddMenuClose = () => {
    setAddMenuAnchor(null);
  };

  // Handle property pin/unpin
  const togglePinProperty = (hotelId: string) => {
    const newPinned = new Set(pinnedProperties);
    if (newPinned.has(hotelId)) {
      newPinned.delete(hotelId);
    } else {
      newPinned.add(hotelId);
    }
    setPinnedProperties(newPinned);
  };

  // Sparkline component
  const Sparkline: React.FC<{ data: number[]; color: string; height?: number }> = ({
    data,
    color,
    height = 20
  }) => {
    const max = Math.max(...data);
    const min = Math.min(...data);
    const range = max - min;

    const points = data.map((value, index) => {
      const x = (index / (data.length - 1)) * 60;
      const y = height - ((value - min) / range) * height;
      return `${x},${y}`;
    }).join(' ');

    return (
      <svg width="60" height={height} style={{ marginLeft: 8 }}>
        <polyline
          fill="none"
          stroke={color}
          strokeWidth="1.5"
          points={points}
        />
      </svg>
    );
  };

  // Enhanced metric card component with sparklines
  const MetricCard: React.FC<{
    title: string;
    value: string | number;
    subtitle?: string;
    icon: React.ElementType;
    trend?: TrendData;
    loading?: boolean;
  }> = ({ title, value, subtitle, icon: Icon, trend, loading = false }) => (
    <Card
      elevation={0}
      sx={{
        minHeight: 160,
        borderRadius: 3,
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        transition: 'all 0.2s ease-in-out',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'stretch',
        '&:hover': {
          borderColor: theme.palette.primary.main,
          boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.1)}`,
          transform: 'translateY(-2px)'
        }
      }}
    >
      <CardContent sx={{ p: 2.5, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
        {loading ? (
          <Box>
            <Skeleton variant="circular" width={48} height={48} sx={{ mb: 2 }} />
            <Skeleton variant="text" width="60%" height={40} sx={{ mb: 1 }} />
            <Skeleton variant="text" width="80%" height={20} />
          </Box>
        ) : (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Avatar
                sx={{
                  width: 48,
                  height: 48,
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                  color: theme.palette.primary.main,
                }}
              >
                <Icon sx={{ fontSize: 24 }} />
              </Avatar>
              {trend && (
                <Tooltip title={`${trend.isPositive ? '+' : ''}${trend.value}% ${trend.previousPeriod}`}>
                  <Chip
                    icon={trend.isPositive ? <TrendingUpIcon /> : <TrendingDownIcon />}
                    label={`${trend.isPositive ? '+' : ''}${trend.value}%`}
                    size="small"
                    color={trend.isPositive ? 'success' : 'error'}
                    variant="outlined"
                    sx={{
                      '& .MuiChip-icon': { fontSize: 16 },
                      cursor: 'pointer'
                    }}
                  />
                </Tooltip>
              )}
            </Box>

            <Typography
              variant="h3"
              sx={{
                fontWeight: 800,
                color: theme.palette.text.primary,
                mb: 0.5,
                fontSize: { xs: '1.8rem', sm: '2.2rem' },
                lineHeight: 1.2
              }}
            >
              {value}
            </Typography>

            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                color: theme.palette.text.primary,
                mb: 0.5,
                fontSize: '1rem'
              }}
            >
              {title}
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1 }}>
              {subtitle && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    fontSize: '0.85rem',
                    lineHeight: 1.3,
                    flex: 1
                  }}
                >
                  {subtitle}
                </Typography>
              )}

              {trend?.sparklineData && (
                <Sparkline
                  data={trend.sparklineData}
                  color={trend.isPositive ? theme.palette.success.main : theme.palette.error.main}
                />
              )}
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  );

  // Sort options
  const sortOptions = [
    { value: 'lastUpdated', label: 'Last Updated' },
    { value: 'highestValue', label: 'Highest Value' },
    { value: 'lowestValue', label: 'Lowest Value' },
    { value: 'name', label: 'Name (A-Z)' },
    { value: 'starRating', label: 'Star Rating' },
    { value: 'rooms', label: 'Number of Rooms' }
  ];

  // If no hotels, show empty state
  if (!hotelsLoading && hotels.length === 0) {
    return (
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Paper
          elevation={0}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 4,
            py: 8,
            px: 4,
            backgroundColor: theme.palette.background.paper,
            borderRadius: 4,
            border: `2px dashed ${theme.palette.divider}`,
          }}
        >
          <Avatar
            sx={{
              width: 80,
              height: 80,
              backgroundColor: alpha(theme.palette.primary.main, 0.1),
              color: theme.palette.primary.main,
            }}
          >
            <HotelIcon sx={{ fontSize: 40 }} />
          </Avatar>

          <Box sx={{ textAlign: 'center', maxWidth: 480 }}>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 2 }}>
              Welcome to Your Portfolio
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4, lineHeight: 1.6 }}>
              Start building your hotel portfolio by adding your first property.
              Track performance metrics, store financial data, and get AI-powered valuation insights.
            </Typography>
          </Box>

          <Button
            variant="contained"
            size="large"
            startIcon={<AddIcon />}
            href="/add-hotel"
            sx={{
              borderRadius: 2,
              px: 4,
              py: 1.5,
              fontSize: '1rem',
              fontWeight: 600
            }}
          >
            Add Your First Hotel
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 2 }}>
      <Fade in={true} timeout={600}>
        <Box>
          {/* Enhanced Header with Actions */}
          <Box sx={{ mb: 2 }}>
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: { xs: 2, sm: 0 }
            }}>
              <Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    color: theme.palette.text.primary,
                    mb: 0.5,
                    fontSize: { xs: '1.5rem', sm: '1.75rem' }
                  }}
                >
                  Portfolio Overview
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    fontWeight: 500,
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  }}
                >
                  {portfolioStats.totalProperties} properties • {formatCurrency(portfolioStats.totalValue, currency)}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {/* Currency Toggle */}
                <FormControlLabel
                  control={
                    <Switch
                      checked={currency === 'EUR'}
                      onChange={(e) => setCurrency(e.target.checked ? 'EUR' : 'USD')}
                      size="small"
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <CurrencyExchangeIcon sx={{ fontSize: 16 }} />
                      <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                        {currency}
                      </Typography>
                    </Box>
                  }
                  sx={{ mr: 1 }}
                />

                {/* Generate Report Button */}
                <Tooltip title="Generate Portfolio Report">
                  <Button
                    variant="outlined"
                    startIcon={<GetAppIcon />}
                    size="small"
                    sx={{
                      borderRadius: 1,
                      px: 2,
                      py: 0.75,
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      borderColor: theme.palette.divider,
                      color: theme.palette.text.secondary,
                      '&:hover': {
                        borderColor: theme.palette.primary.main,
                        backgroundColor: alpha(theme.palette.primary.main, 0.1)
                      }
                    }}
                  >
                    Report
                  </Button>
                </Tooltip>

                <Tooltip title="Refresh Data">
                  <IconButton
                    onClick={handleRefresh}
                    disabled={loading || hotelsLoading}
                    size="small"
                    sx={{
                      backgroundColor: alpha(theme.palette.primary.main, 0.1),
                      color: theme.palette.primary.main,
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.2),
                      }
                    }}
                  >
                    <RefreshIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>

                {/* Enhanced Add Hotel Button with Dropdown */}
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  endIcon={<ArrowDropDownIcon />}
                  onClick={handleAddMenuClick}
                  size="small"
                  sx={{
                    borderRadius: 1,
                    px: 2,
                    py: 0.75,
                    fontWeight: 600,
                    fontSize: '0.875rem'
                  }}
                >
                  Add Hotel
                </Button>

                <Menu
                  anchorEl={addMenuAnchor}
                  open={Boolean(addMenuAnchor)}
                  onClose={handleAddMenuClose}
                  PaperProps={{
                    sx: {
                      mt: 1,
                      borderRadius: 2,
                      boxShadow: theme.shadows[8]
                    }
                  }}
                >
                  <MenuItem onClick={handleAddMenuClose}>
                    <ListItemIcon>
                      <AddIcon />
                    </ListItemIcon>
                    <ListItemText>Manual Entry</ListItemText>
                  </MenuItem>
                  <MenuItem onClick={handleAddMenuClose}>
                    <ListItemIcon>
                      <UploadIcon />
                    </ListItemIcon>
                    <ListItemText>Import from CSV</ListItemText>
                  </MenuItem>
                  <MenuItem onClick={handleAddMenuClose}>
                    <ListItemIcon>
                      <AutoAwesomeIcon />
                    </ListItemIcon>
                    <ListItemText>Use AI Auto-fill</ListItemText>
                  </MenuItem>
                </Menu>
              </Box>
            </Box>

            {/* Loading indicator */}
            {(loading || hotelsLoading) && (
              <LinearProgress
                sx={{
                  borderRadius: 1,
                  height: 2,
                  mt: 1,
                  backgroundColor: alpha(theme.palette.primary.main, 0.1)
                }}
              />
            )}
          </Box>

          {/* AI Suggestions and Alerts */}
          {showAlerts && alerts.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Stack spacing={1}>
                {alerts.slice(0, 2).map((alert) => (
                  <Alert
                    key={alert.id}
                    severity={alert.type as any}
                    variant="outlined"
                    onClose={() => setShowAlerts(false)}
                    action={
                      <Button size="small" color="inherit">
                        {alert.action}
                      </Button>
                    }
                    sx={{
                      borderRadius: 2,
                      '& .MuiAlert-message': { fontSize: '0.875rem' }
                    }}
                  >
                    {alert.message}
                  </Alert>
                ))}
              </Stack>
            </Box>
          )}

          {/* Enhanced Metrics Grid */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            {enhancedMetrics.map((metric, index) => (
              <Grid item xs={12} sm={6} lg={3} key={index}>
                <MetricCard
                  title={metric.title}
                  value={metric.value}
                  subtitle={metric.subtitle}
                  icon={metric.icon}
                  trend={metric.trend}
                  loading={hotelsLoading}
                />
              </Grid>
            ))}
          </Grid>

          {/* Main Content Area */}
          <Paper
            elevation={0}
            sx={{
              borderRadius: 2,
              overflow: 'hidden',
              border: `1px solid ${theme.palette.divider}`,
              backgroundColor: theme.palette.background.paper
            }}
          >
            {/* Enhanced Tab Navigation */}
            <Box sx={{
              borderBottom: `1px solid ${theme.palette.divider}`,
              backgroundColor: alpha(theme.palette.background.default, 0.5)
            }}>
              <Container maxWidth={false} sx={{ px: 0 }}>
                <Tabs
                  value={activeTab}
                  onChange={(e, newValue) => setActiveTab(newValue)}
                  sx={{
                    '& .MuiTab-root': {
                      minHeight: 48,
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      textTransform: 'none',
                      px: 3,
                      py: 1.5,
                      '&.Mui-selected': {
                        color: theme.palette.primary.main,
                      }
                    },
                    '& .MuiTabs-indicator': {
                      height: 2,
                      borderRadius: '2px 2px 0 0'
                    }
                  }}
                >
                  <Tab
                    label="Properties"
                    icon={<HotelIcon sx={{ fontSize: 18 }} />}
                    iconPosition="start"
                  />
                  <Tab
                    label="Analytics"
                    icon={<AnalyticsIcon sx={{ fontSize: 18 }} />}
                    iconPosition="start"
                  />
                  <Tab
                    label="Regions"
                    icon={<LocationOnIcon sx={{ fontSize: 18 }} />}
                    iconPosition="start"
                  />
                  <Tab
                    label="Deal Status"
                    icon={<BusinessCenterIcon sx={{ fontSize: 18 }} />}
                    iconPosition="start"
                  />
                </Tabs>
              </Container>
            </Box>

            {/* Tab Content */}
            <Box sx={{ p: 2 }}>
              {activeTab === 0 && (
                <Box>
                  {/* Enhanced Properties Header with Controls */}
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 2,
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: { xs: 2, sm: 1 }
                  }}>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 600,
                        color: theme.palette.text.primary,
                        fontSize: { xs: '1rem', sm: '1.1rem' }
                      }}
                    >
                      Property List
                    </Typography>

                    <Stack
                      direction="row"
                      spacing={1}
                      sx={{ alignItems: 'center' }}
                    >
                      {/* Sort Dropdown */}
                      <FormControl size="small" sx={{ minWidth: 120 }}>
                        <Select
                          value={sortBy}
                          onChange={(e) => setSortBy(e.target.value)}
                          displayEmpty
                          sx={{ fontSize: '0.875rem' }}
                        >
                          {sortOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      <Divider orientation="vertical" flexItem />

                      {/* View Toggle */}
                      <Tooltip title="Grid View">
                        <IconButton
                          onClick={() => setViewMode('grid')}
                          color={viewMode === 'grid' ? 'primary' : 'default'}
                          size="small"
                          sx={{
                            backgroundColor: viewMode === 'grid'
                              ? alpha(theme.palette.primary.main, 0.1)
                              : 'transparent',
                            '&:hover': {
                              backgroundColor: alpha(theme.palette.primary.main, 0.1)
                            }
                          }}
                        >
                          <ViewModuleIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Table View">
                        <IconButton
                          onClick={() => setViewMode('table')}
                          color={viewMode === 'table' ? 'primary' : 'default'}
                          size="small"
                          sx={{
                            backgroundColor: viewMode === 'table'
                              ? alpha(theme.palette.primary.main, 0.1)
                              : 'transparent',
                            '&:hover': {
                              backgroundColor: alpha(theme.palette.primary.main, 0.1)
                            }
                          }}
                        >
                          <ViewListIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Box>

                  {/* Properties Content */}
                  {viewMode === 'grid' ? (
                    <HotelGrid
                      hotels={hotels}
                      loading={hotelsLoading}
                      pinnedProperties={pinnedProperties}
                      onTogglePin={togglePinProperty}
                      sortBy={sortBy}
                      currency={currency}
                    />
                  ) : (
                    <HotelTable
                      hotels={hotels}
                      loading={hotelsLoading}
                      pinnedProperties={pinnedProperties}
                      onTogglePin={togglePinProperty}
                      sortBy={sortBy}
                      currency={currency}
                    />
                  )}
                </Box>
              )}

              {activeTab === 1 && (
                <PortfolioInsights
                  hotels={hotels}
                  stats={portfolioStats}
                  loading={hotelsLoading}
                />
              )}

              {activeTab === 2 && (
                <Box>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                    Regional Breakdown
                  </Typography>
                  <Typography color="text.secondary">
                    Regional analysis and performance metrics coming soon...
                  </Typography>
                </Box>
              )}

              {activeTab === 3 && (
                <Box>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                    Deal Status Overview
                  </Typography>
                  <Typography color="text.secondary">
                    Deal pipeline and status tracking coming soon...
                  </Typography>
                </Box>
              )}
            </Box>
          </Paper>
        </Box>
      </Fade>
    </Container>
  );
};

export default PortfolioDashboard;